// Fax data access — the `faxes` table plus the three edge functions behind it
// (ingest-faxes, send-fax, assign-fax). See supabase/functions/_shared/fax-common.ts
// for the document-type vocabulary these labels mirror.
import { supabase } from './supabase';

export const DOC_TYPE_LABEL = {
  ppot:              'PPOT',
  lmn:               'LMN',
  medical_records:   'Medical Records',
  pa_decision:       'PA Decision',
  physician_order:   'Physician Order',
  cmn:               'CMN',
  discharge_summary: 'Discharge Summary',
  other:             'Other',
};
export const DOC_TYPES = Object.keys(DOC_TYPE_LABEL);

export const ITEM_LABEL = { ppot: 'PPOT', lmn: 'LMN', records: 'Medical Records' };

export const STATUS_LABEL = {
  matched:   'Filed',
  assigned:  'Filed',
  unmatched: 'Needs review',
  error:     'Error',
  pending:   'Sending',
  success:   'Delivered',
  failure:   'Failed',
};

const CLIENT_COLS = 'clients(id, client_id, first_name, last_name)';

export const getClientFaxes = (clientId) =>
  supabase.from('faxes').select('*')
    .eq('client_id', clientId)
    .order('received_at', { ascending: false, nullsFirst: false })
    .order('sent_at', { ascending: false, nullsFirst: false });

// Everything the inbox shows: unmatched + errored first, then recent traffic.
export const getFaxInbox = (limit = 200) =>
  supabase.from('faxes').select(`*, ${CLIENT_COLS}`)
    .order('created_at', { ascending: false })
    .limit(limit);

async function callFn(name, body) {
  const { data: { session } } = await supabase.auth.getSession();
  const res = await fetch(`${process.env.REACT_APP_SUPABASE_URL}/functions/v1/${name}`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${session?.access_token || process.env.REACT_APP_SUPABASE_ANON_KEY}`,
      apikey: process.env.REACT_APP_SUPABASE_ANON_KEY,
    },
    body: JSON.stringify(body || {}),
  });
  const json = await res.json().catch(() => ({}));
  if (!res.ok || json.ok === false || json.error) throw new Error(json.error || `${name} failed (${res.status})`);
  return json;
}

// Pull now instead of waiting for the hourly cron.
export const runFaxIngest = () => callFn('ingest-faxes', { trigger: 'manual' });

// Sweep faxes received before the cutoff into a Faxage portal folder. Moves
// only — nothing is downloaded or read. Returns { found, moved, remaining }.
// One-off housekeeping (done 2026-09-14); no button calls this any more.
export const archiveOldFaxes = (folder, limit) => callFn('ingest-faxes', { action: 'archive_old', folder, limit });

// { client_id, recipient:{name, practice, fax, kind}, items, request_title,
//   letter_base64, attachments:[{drive_file_id}] }
export const sendFax = (payload) => callFn('send-fax', payload);

export const assignFax = (faxId, clientId, docType) =>
  callFn('assign-fax', { fax_id: faxId, client_id: clientId, doc_type: docType || undefined });

// Undo round-trip links: clear every tick on a sent request, or detach one
// received fax from the request it was counted against.
export const resetRequest = (faxId) => callFn('assign-fax', { action: 'reset_request', fax_id: faxId });
export const unlinkReply  = (faxId) => callFn('assign-fax', { action: 'unlink', fax_id: faxId });

// Admin/manager: remove a fax record and bin its Drive copy (test sends, misdirected faxes).
export const deleteFax = (faxId) => callFn('assign-fax', { action: 'delete', fax_id: faxId });

// Which checklist items on an outbound request are still open.
export const outstandingItems = (fax) => {
  const items = fax?.request_items || {};
  const done = fax?.fulfilled_items || {};
  return Object.keys(items).filter(k => items[k] && !done[k]);
};
